<template>
	<div class="partial-container partial-header grid">
		<div class="header-item">
			<div class="netcallProduct">
				<span class="sr-only" hidden>Liberty Controller</span>
				<span
					v-if="showEnvBadge"
					class="badge badge-danger"
					v-text="envLabel"
				/>
			</div>
		</div>
		<div class="header-item"></div>
		<div class="header-item"></div>
		<div class="header-item no-pointer">
			<notification-tray title="Notification Tray" />
		</div>

		<div class="header-item custom-icon">
			<user-icon title="My Account" @open-account-modal="setAccountModal" />
		</div>

		<edit-account :active="account_modal_active" @change="setAccountModal" />

		<div v-if="isApiLoading" v-cloak id="api-loader" class="loader-container">
			<fa
				class="icon fa-spin"
				title="The request is taking longer than usual. Please wait..."
				:icon="['fas', 'circle-notch']"
			/>
			<p class="hover-text">
				The request is taking longer than usual. Please wait...
			</p>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { inject, ref, computed, onMounted, onUnmounted } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import { useStore } from 'vuex';
	import UserIcon from '../components/UserIcon.vue';
	import NotificationTray from '../components/NotificationTray.vue';
	import EditAccount from '../components/EditAccount.vue';
	import { HttpHelper } from '../helper/http';

	const http = inject<HttpHelper>('http');
	if (!http) throw new Error('HttpHelper (inject \'http\') is not provided');

	const store = useStore();
	const route = useRoute();
	const router = useRouter();

	const account_modal_active = ref(false);

	const isApiLoading = computed<boolean>(() => store.state.api_loading);

	const currentPrefix = computed(() => {
		const path = route.path;

		if (path.startsWith('/ai/')) return 'ai';
		if (path.startsWith('/converse-cx/')) return 'converse';
		if (path.startsWith('/idp/')) return 'idp';
		if (path.startsWith('/spark/')) return 'spark';

		return null;
	});

	const currentEnv = computed(() => {
		const cfg = store.state.configuration;

		switch (currentPrefix.value) {
		case 'ai':
			return cfg.aiEnv;
		case 'converse':
			return cfg.converseEnv;
		case 'idp':
			return cfg.idpEnv;
		case 'spark':
			return cfg.sparkEnv;
		default:
			return null;
		}
	});

	const envLabel = computed(() => {
		if (!currentEnv.value) return '';

		return String(currentEnv.value);
	});

	const showEnvBadge = computed(() => {
		if (!currentPrefix.value) return false;
		if (!currentEnv.value) return false;

		return String(currentEnv.value).toLowerCase() !== 'prod';
	});

	const setAccountModal = (status: boolean): void => {
		account_modal_active.value = status;
	};

	const requestActiveNotifications = async (): Promise<void> => {
		if (!store.state.account.isLoggedIn) return;

		const response = await http.post('/api/notifications', [], { autoRefresh: 'auto' });

		if (response.status === 200) {
			const result = await response.json();
			if (Array.isArray(result) && result.length > 0 && Array.isArray(result[0])) {
				store.dispatch('notification/setActive', result[0]);
			} else {
				store.dispatch('notification/setActive', result);
			}
		}
	};

	const requestAuthAccess = async (): Promise<void> => {
		if (!store.state.account.isLoggedIn) return;

		const accessResponse = await http.post('/api/access', [], { autoRefresh: 'auto' });
		if (accessResponse.status !== 200) return;

		store.dispatch('account/set', await accessResponse.json());
	};

	let intervalId: number | undefined;

	onMounted(() => {
		requestActiveNotifications();
		requestAuthAccess();

		intervalId = window.setInterval(() => {
			void requestActiveNotifications();
			void requestAuthAccess();
		}, 30000);
	});

	onUnmounted(() => {
		if (intervalId !== undefined) clearInterval(intervalId);
	});
</script>


<style lang="scss" scoped>
	@import '../assets/styles/base';
	@import '../assets/styles/partials/header';

	.ai .netcallProduct .badge {
		margin-left: 80px;
	}
	.converse .netcallProduct .badge {
		margin-left: 180px;
	}
	.spark .netcallProduct .badge {
		margin-left: 115px;
	}
	.idp .netcallProduct .badge {
		margin-left: 360px;
	}

	.netcallProduct {
		display: flex;
		align-items: center;
		flex-direction: row;
		gap: 0.5rem;
		.badge {
			text-transform: capitalize;
		}
	}
</style>
